"use client";

import React, { useState, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm, type UseFormReturn } from "react-hook-form";
import { scrollToFirstError } from "@west-tennessee-consulting/ui";
import { zodResolver } from "@/lib/utils/validation/zod-react-hook-form-resolver";
import * as z from "zod";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { ResponsiveCommandSelect } from "@/components/ui/responsive-select";
import { DateTimePicker } from "@/components/ui/date/DateTimePicker";
import { Badge } from "@/components/ui/badge";
import { UserSelect } from "@/components/shared/UserSelect";
import { TagManager } from "./components/TagManager";
import { CheckSquare, Clock, AlertCircle, X } from "lucide-react";
import { FormSectionGroup } from "@/components/shared/forms/FormSectionGroup";
import { defineTaxonomySection } from "@/components/shared/forms/FormSectionGroup/section-taxonomy";
import { TASK_STATUS_VALUES, TASK_PRIORITY_VALUES } from "@/lib/constants/task-enums";

// Task-specific schema - comprehensive with all database fields
const taskSchema = z.object({
  title: z.string().min(1, "Title is required."),
  description: z.string().optional(),
  status: z.enum(TASK_STATUS_VALUES),
  priority: z.enum(TASK_PRIORITY_VALUES),
  due_date: z.date().optional(),
  assigned_to: z.string().optional(),
  tags: z.array(z.string()).optional(),
  completed_at: z.date().optional(),
});

type TaskFormValues = z.infer<typeof taskSchema>;
export type TaskFormSubmission = TaskFormValues;

interface TaskFormProps {
  onSubmit: (values: TaskFormSubmission) => Promise<void>;
  isSubmitting: boolean;
}

const TASK_STATUS_OPTIONS = [
  { value: "pending", label: "Pending", color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200", icon: Clock },
  {
    value: "in_progress",
    label: "In Progress",
    color: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200",
    icon: CheckSquare,
  },
  {
    value: "completed",
    label: "Completed",
    color: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200",
    icon: CheckSquare,
  },
  { value: "cancelled", label: "Cancelled", color: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200", icon: X },
];

const TASK_PRIORITY_OPTIONS = [
  {
    value: "low",
    label: "Low Priority",
    color: "bg-card-100 text-gray-800 dark:text-gray-200",
    description: "Can be done when time allows",
  },
  {
    value: "medium",
    label: "Medium Priority",
    color: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200",
    description: "Normal priority task",
  },
  {
    value: "high",
    label: "High Priority",
    color: "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200",
    description: "Important, needs attention soon",
  },
  {
    value: "urgent",
    label: "Urgent",
    color: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200",
    description: "Drop everything — highest priority",
  },
];

export const TaskForm: React.FC<TaskFormProps> = ({ onSubmit, isSubmitting }) => {
  const [error, setError] = useState<string | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const form = useForm<TaskFormValues>({
    resolver: zodResolver(taskSchema),
    defaultValues: {
      title: "",
      description: "",
      status: "pending",
      priority: "medium",
      tags: [],
    },
  });

  const watchedStatus = form.watch("status");

  const statusOptions = useMemo(
    () => TASK_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
    []
  );

  const priorityOptions = useMemo(
    () =>
      TASK_PRIORITY_OPTIONS.map((o) => ({
        value: o.value,
        label: o.label,
        description: o.description,
      })),
    []
  );

  const handleUserSelect = useCallback(
    async (userId: string) => {
      form.setValue("assigned_to", userId === "unassigned" ? undefined : userId);
    },
    [form]
  );

  const handleSubmit = useCallback(
    async (values: TaskFormValues) => {
      setError(null);
      try {
        await onSubmit({
          ...values,
          tags,
          completed_at: values.status === "completed" ? new Date() : undefined,
        });
        form.reset();
        setTags([]);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Failed to create task");
      }
    },
    [tags, onSubmit, form]
  );

  // Progressive-disclosure section layout (plan
  // `the-log-activity-modal-enumerated-whisper.md`).
  const sections = useMemo(
    () => [
      defineTaxonomySection<TaskFormValues>("core", {
        title: "Core details",
        summary: "Title, status, priority",
        kind: "always",
        render: (f) => (
          <div className="space-y-4">
            <FormField
              control={f.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Task Title</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="What needs to be done?"
                      {...field}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={f.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <FormControl>
                      <ResponsiveCommandSelect
                        value={field.value}
                        onValueChange={field.onChange}
                        options={statusOptions}
                        disabled={isSubmitting}
                        title="Task Status"
                        description="Current status of this task"
                        placeholder="Select status"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={f.control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Priority</FormLabel>
                    <FormControl>
                      <ResponsiveCommandSelect
                        value={field.value}
                        onValueChange={field.onChange}
                        options={priorityOptions}
                        disabled={isSubmitting}
                        title="Task Priority"
                        description="Set the priority level for this task"
                        placeholder="Select priority"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        ),
      }),
      defineTaxonomySection<TaskFormValues>("scheduling", {
        summary: "Due date and assignee",
        hasValue: (f) => Boolean(f.watch("due_date") || f.watch("assigned_to")),
        defaultOpen: Boolean(form.getValues("due_date") || form.getValues("assigned_to")),
        render: (f) => (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={f.control}
              name="due_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Due Date</FormLabel>
                  <FormControl>
                    <DateTimePicker value={field.value} onChange={field.onChange} />
                  </FormControl>
                  <FormDescription>When should this task be completed?</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={f.control}
              name="assigned_to"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Assign To</FormLabel>
                  <FormControl>
                    <UserSelect
                      currentUserId={field.value}
                      onUserSelect={handleUserSelect}
                      disabled={isSubmitting}
                      placeholder="Assign to..."
                      showUnassigned={true}
                    />
                  </FormControl>
                  <FormDescription>Who is responsible for this task?</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        ),
      }),
      defineTaxonomySection<TaskFormValues>("description", {
        summary: "Notes and tags",
        hasValue: (f) => Boolean(f.watch("description")) || tags.length > 0,
        defaultOpen: Boolean(form.getValues("description")) || tags.length > 0,
        render: (f) => (
          <div className="space-y-4">
            <FormField
              control={f.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Provide additional details..."
                      className="min-h-[100px] resize-y"
                      {...field}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormDescription>Additional details for the task</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <TagManager tags={tags} setTags={setTags} disabled={isSubmitting} />
          </div>
        ),
      }),
    ],
    // Section descriptors capture defaultOpen/hasValue from outer state on
    // mount; FormSectionGroup itself memoizes the initial-open set so
    // subsequent rebuilds won't snap sections against the user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSubmitting, statusOptions, priorityOptions, handleUserSelect, tags]
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit, scrollToFirstError)} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg dark:bg-red-950/40 dark:text-red-300">
            <AlertCircle size={16} />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <FormSectionGroup
          form={form as UseFormReturn<TaskFormValues>}
          sections={sections}
        />

        {watchedStatus && (
          <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
            <Badge className={TASK_STATUS_OPTIONS.find((s) => s.value === watchedStatus)?.color}>
              {TASK_STATUS_OPTIONS.find((s) => s.value === watchedStatus)?.label}
            </Badge>
          </div>
        )}

        <div className="flex justify-end pt-4">
          <Button type="submit" disabled={isSubmitting} className="min-w-[120px]">
            {isSubmitting ? (
              <>
                <LoadingSpinner size="sm" /> Creating...
              </>
            ) : (
              "Create Task"
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};
